"use client";

/**
 * Top-level error boundary for the App Router.
 *
 * Catches uncaught errors during page render: client-side hydration
 * mishaps, transient server failures bubbling through Suspense,
 * server-action throws that propagate up past their callers. The
 * companion `global-error.tsx` is the fallback for errors in the
 * root layout itself (where this boundary can't mount).
 *
 * Visual treatment matches the rest of the webapp — paper bg,
 * hairline rule borders, mono caps eyebrow, square corners,
 * brand-tone alert red. `data-app="dashboard"` re-activates the
 * shadcn token bridge so the nested <Button> primitives resolve to
 * the brand palette (this page renders outside the dashboard layout
 * so it wouldn't otherwise inherit the scope).
 *
 * What we show vs hide:
 *   - error.message: always shown. Most errors here are network /
 *     hydration adjacent — typically safe to surface, and helpful
 *     when a user wants to copy text into a bug report.
 *   - error.stack: development only. Production stacks expose
 *     server internals + minified frames that aren't useful to
 *     end users anyway.
 *   - error.digest: always shown when present. Next.js generates
 *     it for server errors and it's the only handle for engineers
 *     to trace a specific failure in logs.
 */

import { usePathname, useRouter } from "next/navigation";
import { useEffect, startTransition } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const IS_DEV = process.env.NODE_ENV === "development";

/**
 * Tell when an error is one that `reset()` CAN'T fix — the user
 * either has a tab open from a previous deploy and the JS chunks
 * it references have been replaced, OR a webpack module factory
 * is missing entirely so calling `__webpack_require__` blows up
 * mid-hydration. In both cases re-rendering against the same
 * (now-broken) module URLs just throws the same error. A hard
 * reload is the right move: the browser fetches the fresh document
 * and the current chunks come with it.
 *
 * Error shapes seen in the wild:
 *   - "Loading chunk 2649 failed."
 *   - "ChunkLoadError: Loading chunk 0 failed"
 *   - "Failed to fetch dynamically imported module: …"
 *   - "Importing a module script failed."
 *   - "Cannot read properties of undefined (reading 'call')"
 *       ← webpack's `__webpack_require__.call(...)` when the module
 *         factory is undefined. Almost always a stale-bundle /
 *         missing-chunk symptom; reset() loops.
 *   - "TypeError: …call is not a function" — same root cause, the
 *     factory exists but isn't callable (module-shape mismatch).
 */
function isUnrecoverableError(error: Error): boolean {
  const msg = error.message || "";
  return (
    /Loading chunk \w+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Cannot read properties of undefined \(reading 'call'\)/i.test(msg) ||
    /undefined is not a function/i.test(msg) ||
    error.name === "ChunkLoadError"
  );
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("App error:", error);
  }, [error]);

  const hardReload = isUnrecoverableError(error);
  const pathname = usePathname();
  const router = useRouter();

  // The Try-again link points at the current path with a cache-
  // busting query so the browser refetches the document instead
  // of restoring from bfcache (which would re-trigger the same
  // hydration error). Falls back to "/" for the unusual case
  // where pathname isn't available.
  const tryAgainHref = `${pathname || "/"}${(pathname || "/").includes("?") ? "&" : "?"}_=${Date.now()}`;

  // "Home" for someone inside the authenticated app is the dashboard,
  // NOT "/". On the app host (app.pcnshark.com) the middleware
  // unconditionally 302s "/" → "/login" — it deliberately doesn't
  // probe the session (see middleware.ts), so linking home to "/"
  // bounces a logged-in user straight to the login page. Routing
  // dashboard/staff errors to "/dashboard" lands authed users on
  // their dashboard and still does the right thing for an expired
  // session (middleware redirects to /login with a redirectTo).
  // Marketing-surface errors keep "/" as home.
  const inApp =
    !!pathname &&
    (pathname.startsWith("/dashboard") || pathname.startsWith("/staff"));
  const homeHref = inApp ? "/dashboard" : "/";

  // CRITICAL: both buttons render as real anchors so they work
  // even when hydration is broken. The original implementation
  // used `<Button onClick>` for everything, but webpack-module
  // failures (the most common cause of getting here) prevent
  // React from attaching event handlers to this error boundary's
  // subtree — buttons rendered as visual HTML with dead clicks.
  // Anchors with `href` work via the browser's default navigation
  // whether JS is alive or not.
  //
  // We still attach `onClick` so we can call reset() on the happy
  // path (when hydration works and the error IS recoverable). The
  // handler calls preventDefault() so the navigation doesn't
  // double-fire; if the handler never binds (dead hydration), the
  // anchor's native href takes over.
  const onTryAgainClick = (e: React.MouseEvent) => {
    if (hardReload) {
      // Let the anchor's href do its thing (cache-busted reload).
      return;
    }
    e.preventDefault();
    // reset() alone re-renders this boundary against the CACHED
    // Server Component payload — for a server-thrown error that's the
    // same errored output, so it re-throws immediately and the page
    // looks stuck ("Try again does nothing"). router.refresh()
    // refetches the RSC tree from the server first; pairing them in a
    // transition re-runs the render against fresh data so a transient
    // failure actually clears. If hydration is dead this handler never
    // binds and the anchor's href (cache-busted reload) takes over.
    startTransition(() => {
      router.refresh();
      reset();
    });
  };

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ backgroundColor: "var(--paper)" }}
    >
      <div data-app="dashboard" className="w-full max-w-xl space-y-5">
        {/* Eyebrow — red caps-mono signals the failure mode without
            shouting. Same vocabulary used for alert pills + the
            stuck-upload banner. */}
        <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--shark-alert)]">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          Error
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--ink)]">
          Something went wrong
        </h1>

        {/* Body copy — matter-of-fact, no jargon. The action buttons
            below tell the user what to do next; the prose just
            frames the situation. */}
        <p className="max-w-prose text-[13px] leading-relaxed text-[color:var(--ink-2)]">
          {hardReload ? (
            <>
              A new version of PCNshark shipped while you had this tab
              open, so some of the page&rsquo;s code is out of date.
              Reload to pick up the fresh build.
            </>
          ) : (
            <>
              We hit a snag rendering this page. Trying again usually
              clears it up — if it doesn&rsquo;t, head home and
              we&rsquo;ll investigate.
            </>
          )}
        </p>

        {/* Error details panel — hairline-bordered card with the
            cream paper-2 surface, mirroring the BOM detail page's
            empty-state cards. Mono text inside for the literal error
            content; max-h-64 with overflow-auto so long stacks don't
            blow out the layout. */}
        {error.message && (
          <div
            className="border px-4 py-3"
            style={{
              backgroundColor: "var(--paper-2)",
              borderColor: "var(--rule)",
            }}
          >
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--shark-alert)]">
              Error details
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11.5px] leading-relaxed text-[color:var(--ink-2)]">
              {error.message}
              {IS_DEV && error.stack ? "\n\n" + error.stack : ""}
            </pre>
          </div>
        )}

        {/* Trace ID — surfaces error.digest as a tag the user can
            copy into a bug report. Mono caps label + normal-case
            mono value, matching the audit-footer pattern on the
            team-note card. */}
        {error.digest && (
          <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[color:var(--ink-3)]">
            Trace ID
            <span className="ml-2 font-normal tracking-normal normal-case text-[color:var(--ink-2)]">
              {error.digest}
            </span>
          </p>
        )}

        {/* Actions — anchors, not buttons. See the comment on
            `onTryAgainClick` above for why this matters: when the
            triggering error broke React's hydration for this
            subtree (the common webpack-module-call failure mode),
            <button onClick> never gets wired up and clicking does
            nothing. Anchors with href navigate via the browser's
            default behaviour regardless of JS state. */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button asChild>
            <a href={tryAgainHref} onClick={onTryAgainClick}>
              {hardReload ? "Reload" : "Try again"}
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={homeHref}>Go home</a>
          </Button>
        </div>
      </div>
    </main>
  );
}
